import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MessageCircle, ArrowDown, ArrowRight, GraduationCap, Users, User, Heart,
  BookOpen, Clock, Monitor, Sparkles, ShieldCheck, Baby, Briefcase, Star,
} from "lucide-react";
import { BRAND } from "../lib/brand";
import { initMetaPixel, trackWhatsAppContact } from "../lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- WhatsApp helpers (messaggi approvati da Teresa, mai prezzi) ---
const WA_MSGS = {
  pro: "Buongiorno Teresa, ho visitato la pagina Become Tata Like a Pro e vorrei ricevere il programma completo e le informazioni relative a modalità di partecipazione, calendario e costi. Grazie!",
  brand: "Buongiorno Teresa, vorrei ricevere informazioni sul percorso Become Tata Like a Pro con Tata Brand, comprese modalità di partecipazione e costi. Grazie!",
};
const waLink = (kind, utmSource) => {
  let msg = WA_MSGS[kind];
  if (utmSource) msg += `\n\n(Ti scrivo dopo aver visto la pagina tramite ${utmSource})`;
  return `${BRAND.whatsappUrl}?text=${encodeURIComponent(msg)}`;
};

const MODULES = [
  { t: "Il ruolo e l'identità della tata professionista", d: "Responsabilità, competenze, confini e specificità del lavoro educativo a domicilio." },
  { t: "Lo sviluppo del bambino da 0 a 36 mesi", d: "Sviluppo motorio, cognitivo, emotivo, relazionale e comunicativo." },
  { t: "Le routine e i momenti di cura", d: "Accoglienza, separazione, igiene, cambio, pasti, sonno e transizioni." },
  { t: "Comunicazione e alleanza educativa con la famiglia", d: "Relazione professionale, aspettative, confini, comunicazione e restituzione." },
  { t: "Sicurezza e gestione delle emergenze", d: "Prevenzione, gestione dei rischi, organizzazione degli ambienti e procedure appropriate." },
  { t: "Gioco, osservazione e attività educative", d: "Gioco libero, materiali, proposte intenzionali e osservazione." },
  { t: "Il sonno nei primi tre anni", d: "Bisogni, segnali, rituali e accompagnamento rispettoso dell'addormentamento." },
  { t: "Alimentazione e accompagnamento alle autonomie", d: "Alimentazione complementare, relazione con il cibo, abbandono graduale del ciuccio e del pannolino." },
  { t: "Benessere, movimento e psicomotricità", d: "Esperienze corporee, sensoriali, motorie e attività adeguate allo sviluppo." },
  { t: "Strumenti e organizzazione professionale", d: "Presentazione delle competenze, organizzazione del lavoro, strumenti professionali e piano di sviluppo." },
];

const AUDIENCE = [
  { icon: Briefcase, t: "Tate che già lavorano", d: "Per chi accompagna bambini ogni giorno e desidera approfondire le proprie competenze con strumenti concreti." },
  { icon: Sparkles, t: "Chi vuole iniziare", d: "Per chi sente che la cura dei bambini è la sua strada e vuole costruire una professionalità solida, passo dopo passo." },
  { icon: GraduationCap, t: "Educatrici e professioniste dell'infanzia", d: "Per chi arriva dai servizi educativi e vuole portare la propria competenza nel lavoro educativo a domicilio." },
];

const WHY = [
  { icon: Heart, t: "Ventitré anni di esperienza diretta", d: "Ogni contenuto nasce dal lavoro quotidiano con bambini e famiglie: niente teoria astratta, solo ciò che serve davvero." },
  { icon: BookOpen, t: "Teoria e pratica insieme", d: "Casi educativi reali, simulazioni ed esercitazioni: impari a osservare, decidere e agire." },
  { icon: Users, t: "Piccolo gruppo, grande ascolto", d: "Massimo 8 partecipanti: c'è spazio per le tue domande, le tue esperienze e il tuo modo di lavorare." },
  { icon: Baby, t: "Focus 0–36 mesi", d: "La fascia d'età più delicata e più richiesta dalle famiglie, trattata in modo approfondito." },
];

const MATERIALS = [
  "Materiali didattici per ogni modulo",
  "Esercitazioni pratiche tra un incontro e l'altro",
  "Strumenti operativi da usare subito con le famiglie",
  "Casi educativi reali e simulazioni guidate",
];

const FAQ = [
  { q: "Serve esperienza per partecipare?", a: "No. Il percorso accoglie sia chi lavora già come tata sia chi vuole iniziare: le competenze si costruiscono passo dopo passo, partendo da dove sei." },
  { q: "È un titolo abilitante o riconosciuto?", a: "No: Become Tata Like a Pro è un percorso di formazione professionale, non un titolo abilitante. Ti dà strumenti, conoscenze e metodo per lavorare con competenza e sicurezza." },
  { q: "Come funziona la formazione online?", a: "Ci incontriamo in diretta la sera, due ore per incontro: spiegazioni, casi educativi, simulazioni e spazio per le domande. Tra un incontro e l'altro, piccole esercitazioni pratiche." },
  { q: "Posso seguire il percorso individualmente?", a: "Sì: lo stesso programma è disponibile anche in modalità individuale, con un calendario concordato insieme in base alle tue esigenze." },
  { q: "Tata Brand è obbligatorio?", a: "No: è un percorso aggiuntivo facoltativo di 4 ore, pensato per chi vuole valorizzare la propria identità professionale. Puoi aggiungerlo a Become Tata Like a Pro oppure valutarlo in seguito." },
  { q: "Come ricevo programma completo, calendario e costi?", a: "Scrivimi su WhatsApp con il pulsante qui sotto: ti rispondo personalmente con il programma dettagliato, le modalità di partecipazione e tutte le informazioni economiche." },
];

const WaButton = ({ kind, utm, label, variant = "primary", testId }) => (
  <a
    href={waLink(kind, utm)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    onClick={() => trackWhatsAppContact(kind === "brand" ? "Tata Brand" : "Become Tata Like a Pro")}
    className={variant === "primary"
      ? "inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#FF2768] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#c81e52] transition-colors shadow-lg w-full sm:w-auto"
      : "inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-[#3D2140] text-[#3D2140] font-bold text-sm uppercase tracking-wider hover:bg-[#3D2140] hover:text-white transition-colors w-full sm:w-auto"}
  >
    <MessageCircle size={17} /> {label}
  </a>
);

const Section = ({ eyebrow, title, children, dark = false, testId }) => (
  <section className={`px-6 md:px-10 py-16 md:py-24 ${dark ? "bg-[#3D2140] text-white" : ""}`} data-testid={testId}>
    <div className="max-w-5xl mx-auto">
      {eyebrow && <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold mb-4">{eyebrow}</p>}
      {title && <h2 className={`font-display text-4xl md:text-6xl leading-[1.02] mb-10 ${dark ? "text-white" : "text-[#3D2140]"}`}>{title}</h2>}
      {children}
    </div>
  </section>
);

export default function BecomeTataPro() {
  const [edition, setEdition] = useState(null);
  const [notLive, setNotLive] = useState(false);

  const utm = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("utm_source") || "";
  }, []);

  useEffect(() => {
    axios.get(`${API}/tata-landing`)
      .then((r) => {
        setEdition(r.data);
        applySeo();
        initMetaPixel();
      })
      .catch((err) => { if (err.response?.status === 404) setNotLive(true); });
  }, []);

  const applySeo = () => {
    // Impostato dopo il fetch: AppShell sovrascrive document.title al mount
    document.title = "Become Tata Like a Pro | Formazione online per tate — Teresa Vilardo";
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", "Become Tata Like a Pro: 22 ore di formazione online per tate e professioniste dell'infanzia. Piccoli gruppi, casi educativi e strumenti pratici.");
    setMeta("property", "og:title", "Become Tata Like a Pro | Formazione online per tate — Teresa Vilardo");
    setMeta("property", "og:description", "22 ore di formazione online per tate e professioniste dell'infanzia. Piccoli gruppi, casi educativi e strumenti pratici.");
    setMeta("property", "og:url", window.location.href);
    setMeta("property", "og:image", BRAND.logo);
  };

  if (notLive) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center" data-testid="tata-landing-hidden">
        <Sparkles className="text-[#FF2768] mb-4" size={32} />
        <h1 className="font-display text-4xl text-[#3D2140] mb-2">Qualcosa di speciale sta arrivando…</h1>
        <p className="text-[#3D2140]/60 max-w-md">Questa pagina sarà disponibile a breve. Nel frattempo, scopri la T.A.T.A. Academy.</p>
      </div>
    );
  }
  if (!edition) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#3D2140]/50">Un attimo…</div>;
  }

  return (
    <div className="bg-[#FAF8F5]" data-testid="tata-landing-page">

      {/* 1. HERO */}
      <section className="px-6 md:px-10 pt-28 md:pt-36 pb-16 md:pb-24 relative overflow-hidden" data-testid="hero">
        <div className="max-w-5xl mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold mb-5">T.A.T.A. Academy · Formazione professionale per tate</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-[#3D2140] leading-[0.95] mb-6">
            Become Tata<br /><span className="text-[#FF2768]">Like a Pro</span>
          </h1>
          <p className="font-editorial italic text-2xl md:text-3xl text-[#3D2140]/85 mb-10 max-w-2xl">
            Dalla cura quotidiana alla professionalità educativa.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <WaButton kind="pro" utm={utm} label="Richiedi informazioni" testId="cta-hero" />
            <a href="#programma" data-testid="cta-scroll-programma" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-[#3D2140] text-[#3D2140] font-bold text-sm uppercase tracking-wider hover:bg-[#3D2140] hover:text-white transition-colors">
              Scopri il programma <ArrowDown size={16} />
            </a>
          </div>
        </div>
        <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-8 md:right-10 -bottom-10 md:bottom-4 h-36 md:h-52 w-36 md:w-52 rounded-full opacity-70 pointer-events-none" />
      </section>

      {/* 2. PRESENTAZIONE */}
      <Section testId="presentazione">
        <div className="max-w-3xl space-y-5 text-lg md:text-xl leading-relaxed text-[#3D2140]/85">
          <p>Fare la tata significa molto più che stare con i bambini.</p>
          <p>Significa conoscere i loro bisogni, comprenderne lo sviluppo, rispettarne i tempi, accompagnare le autonomie e costruire relazioni di fiducia con le famiglie.</p>
          <p><strong className="text-[#3D2140]">Become Tata Like a Pro</strong> nasce da oltre 23 anni di esperienza educativa e dal desiderio di condividere strumenti, conoscenze e competenze con chi vuole iniziare o crescere professionalmente nel lavoro di tata.</p>
          <p className="font-editorial italic text-[#FF2768]">Un percorso che unisce teoria, esperienza, osservazione e pratica educativa.</p>
        </div>
        <div className="flex flex-wrap gap-x-10 gap-y-4 mt-12" data-testid="facts">
          {[["22", "ore complessive"], ["11", "incontri da 2 ore"], ["10", "moduli formativi"], ["0–36", "mesi: il focus educativo"]].map(([n, l]) => (
            <div key={l} className="flex items-baseline gap-3">
              <span className="font-display text-4xl md:text-5xl text-[#FF2768]">{n}</span>
              <span className="text-sm text-[#3D2140]/70 max-w-[120px] leading-tight">{l}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 3. A CHI È RIVOLTO */}
      <Section eyebrow="— A chi è rivolto" title={<>Pensato per chi vuole<br /><span className="text-[#FF2768]">crescere con cura.</span></>} dark testId="audience">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {AUDIENCE.map((a, i) => (
            <motion.div key={a.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-white/10 backdrop-blur rounded-3xl p-7 border border-white/15" data-testid={`audience-${i}`}>
              <span className="h-12 w-12 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-5"><a.icon size={20} /></span>
              <h3 className="font-display text-2xl mb-3">{a.t}</h3>
              <p className="text-white/75 leading-relaxed text-[15px]">{a.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* 4. PERCHÉ SCEGLIERE */}
      <Section eyebrow="— Perché questa formazione" title={<>La pratica prima<br /><span className="text-[#FF2768]">di tutto.</span></>} testId="why">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {WHY.map((w, i) => (
            <div key={w.t} className="bg-white rounded-3xl p-7 border border-[#3D2140]/10 flex gap-5" data-testid={`why-${i}`}>
              <span className="shrink-0 h-12 w-12 rounded-2xl bg-[#81C3C9] text-white flex items-center justify-center"><w.icon size={20} /></span>
              <div>
                <h3 className="font-display text-2xl text-[#3D2140] mb-2">{w.t}</h3>
                <p className="text-[#3D2140]/70 leading-relaxed text-[15px]">{w.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. PROGRAMMA — 10 MODULI */}
      <Section eyebrow="— Il programma" title={<>Dieci moduli,<br /><span className="text-[#FF2768]">un metodo completo.</span></>} testId="programma" >
        <div id="programma" className="space-y-3">
          {MODULES.map((m, i) => (
            <motion.div key={m.t} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="bg-white rounded-2xl p-5 md:p-6 border border-[#3D2140]/10 flex gap-5 items-start hover:border-[#FF2768]/50 transition-colors" data-testid={`module-${i + 1}`}>
              <span className="shrink-0 font-editorial italic text-2xl text-[#FF2768] w-10">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-display text-xl md:text-2xl text-[#3D2140] mb-1">{m.t}</h3>
                <p className="text-[#3D2140]/70 text-[15px] leading-relaxed">{m.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 bg-[#FFE4E8]/60 rounded-3xl p-8 text-center">
          <p className="font-editorial italic text-xl text-[#3D2140] mb-6">Vuoi il programma completo con tutti i dettagli?</p>
          <WaButton kind="pro" utm={utm} label="Scopri il percorso completo" testId="cta-after-programma" />
        </div>
      </Section>

      {/* 6. COME SI SVOLGE + PROSSIMA EDIZIONE */}
      <Section eyebrow="— Come si svolge" title={<>Online, la sera,<br /><span className="text-[#FF2768]">in diretta con me.</span></>} dark testId="come-funziona">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          {[
            { icon: Monitor, t: "Online in diretta", d: "Lezioni dal vivo, con spazio per domande, confronto e simulazioni guidate." },
            { icon: Clock, t: "11 incontri da 2 ore", d: "22 ore complessive di formazione, la sera, compatibili con il tuo lavoro." },
          ].map((f, i) => (
            <div key={f.t} className="bg-white/10 rounded-3xl p-7 border border-white/15" data-testid={`how-${i}`}>
              <span className="h-12 w-12 rounded-2xl bg-[#81C3C9] text-white flex items-center justify-center mb-5"><f.icon size={20} /></span>
              <h3 className="font-display text-2xl mb-2">{f.t}</h3>
              <p className="text-white/75 text-[15px] leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#FFE45C] text-[#3D2140] rounded-3xl p-8 md:p-10" data-testid="edition-card">
          <p className="text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Prossima edizione — piccolo gruppo online</p>
          {edition.edition_period ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div><p className="text-xs uppercase tracking-widest opacity-60 mb-1">Periodo</p><p className="font-display text-xl leading-tight" data-testid="edition-period">{edition.edition_period}</p></div>
                <div><p className="text-xs uppercase tracking-widest opacity-60 mb-1">Giorni</p><p className="font-display text-xl leading-tight" data-testid="edition-days">{edition.edition_days}</p></div>
                <div><p className="text-xs uppercase tracking-widest opacity-60 mb-1">Orario</p><p className="font-display text-xl leading-tight" data-testid="edition-time">{edition.edition_time}</p></div>
                <div><p className="text-xs uppercase tracking-widest opacity-60 mb-1">Posti</p><p className="font-display text-xl leading-tight">Massimo 8 partecipanti</p></div>
              </div>
              <p className="text-sm mt-6 opacity-70 italic">{edition.edition_note}</p>
            </>
          ) : (
            <div className="text-center py-2" data-testid="edition-ask">
              <p className="font-display text-2xl md:text-3xl mb-3">La prossima edizione è in programmazione</p>
              <p className="text-[15px] opacity-75 max-w-xl mx-auto mb-6">
                Scrivimi su WhatsApp: ti dirò quando parte la prossima edizione e ti riservo tutte le informazioni per partecipare. I posti sono al massimo 8.
              </p>
              <a
                href={waLink("pro", utm)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-edition-ask"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#3D2140] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#FF2768] transition-colors"
              >
                <MessageCircle size={17} /> Quando parte la prossima edizione?
              </a>
            </div>
          )}
        </div>
      </Section>

      {/* 7. LA FORMATRICE */}
      <Section eyebrow="— La tua formatrice" title={<>Ciao, sono<br /><span className="text-[#FF2768]">Teresa.</span></>} testId="formatrice">
        <div className="flex flex-col md:flex-row gap-10 items-start">
          <img src={BRAND.photoChiSono} alt="Teresa Vilardo" className="w-full md:w-72 aspect-[4/5] object-cover rounded-[32px] border-4 border-white shadow-xl shrink-0" data-testid="teresa-photo" />
          <div className="space-y-4 text-[#3D2140]/85 leading-relaxed text-lg">
            <p><strong className="text-[#3D2140]">Teresa Vilardo</strong> — educatrice nei servizi per l'infanzia, laureata L-19, tata professionista con oltre 23 anni di esperienza, Family Coach Montessori, educatrice neonatale, operatrice in psicomotricità e massaggio infantile, consulente per genitori e formatrice di tate.</p>
            <p>Sono l'ideatrice dell'Academy T.A.T.A. e del percorso Become Tata Like a Pro.</p>
            <p>Quello che insegno nasce dal lavoro vero: ventitré anni a fianco dei bambini e delle loro famiglie. Il mio approccio unisce il rispetto educativo dei tempi di ogni bambino al collegamento costante tra teoria e pratica — perché una tata professionista non improvvisa: osserva, comprende e sa cosa fare.</p>
          </div>
        </div>
      </Section>

      {/* 8. MATERIALI E STRUMENTI */}
      <Section eyebrow="— Cosa ricevi" title={<>Materiali, strumenti<br /><span className="text-[#FF2768]">ed esercitazioni.</span></>} testId="materiali">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
          {MATERIALS.map((m, i) => (
            <div key={m} className="flex items-start gap-3 bg-white rounded-2xl p-5 border border-[#3D2140]/10" data-testid={`material-${i}`}>
              <Star className="text-[#FF2768] shrink-0 mt-0.5" size={18} />
              <p className="text-[#3D2140]/85 text-[15px]">{m}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 9. TATA BRAND */}
      <Section dark testId="tata-brand">
        <div className="bg-gradient-to-br from-[#5A3660] to-[#3D2140] rounded-[32px] p-8 md:p-12 border border-[#FF2768]/30">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] font-semibold mb-3">Percorso aggiuntivo · facoltativo</p>
          <h2 className="font-display text-4xl md:text-5xl mb-5">Tata Brand</h2>
          <p className="text-white/80 text-lg leading-relaxed max-w-2xl mb-8">
            Quattro ore dedicate alla tua identità professionale: due incontri per valorizzare le tue competenze, curare la tua presentazione professionale e imparare a comunicare il tuo valore alle famiglie. Strumenti concreti per costruire e valorizzare il tuo profilo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 max-w-2xl">
            <div className="bg-white/10 rounded-2xl p-5">
              <p className="font-display text-2xl mb-1">Become Tata Like a Pro</p>
              <p className="text-white/70 text-sm">22 ore · il percorso completo</p>
            </div>
            <div className="bg-[#FF2768] rounded-2xl p-5">
              <p className="font-display text-2xl mb-1">+ Tata Brand</p>
              <p className="text-white/85 text-sm">26 ore complessive · percorso + identità professionale</p>
            </div>
          </div>
          <WaButton kind="brand" utm={utm} label="Chiedi informazioni su Tata Brand" variant="primary" testId="cta-tata-brand" />
        </div>
      </Section>

      {/* 10. GRUPPO O INDIVIDUALE */}
      <Section eyebrow="— Due modi per partecipare" title={<>In piccolo gruppo<br /><span className="text-[#FF2768]">oppure solo tu ed io.</span></>} testId="modalita">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-3xl p-8 border-2 border-[#FF2768] relative" data-testid="modalita-gruppo">
            <span className="absolute -top-3 left-6 bg-[#FF2768] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">Prossima edizione</span>
            <Users className="text-[#FF2768] mb-4" size={26} />
            <h3 className="font-display text-3xl text-[#3D2140] mb-3">Piccolo gruppo online</h3>
            <ul className="space-y-2 text-[#3D2140]/80 text-[15px] mb-6">
              <li>· Massimo 8 partecipanti</li>
              {edition.edition_period ? (
                <>
                  <li>· {edition.edition_days}, {edition.edition_time}</li>
                  <li>· {edition.edition_period}</li>
                </>
              ) : (
                <li>· Calendario in definizione: scrivimi per sapere quando parte la prossima edizione</li>
              )}
              <li>· Confronto e casi condivisi con le altre partecipanti</li>
            </ul>
            <WaButton kind="pro" utm={utm} label="Richiedi informazioni" testId="cta-gruppo" />
          </div>
          <div className="bg-white rounded-3xl p-8 border border-[#3D2140]/10" data-testid="modalita-individuale">
            <User className="text-[#81C3C9] mb-4" size={26} />
            <h3 className="font-display text-3xl text-[#3D2140] mb-3">Percorso individuale</h3>
            <ul className="space-y-2 text-[#3D2140]/80 text-[15px] mb-6">
              <li>· Lo stesso programma completo, solo per te</li>
              <li>· Calendario flessibile, concordato insieme</li>
              <li>· Attenzione interamente dedicata ai tuoi obiettivi</li>
              <li>· Disponibile tutto l'anno</li>
            </ul>
            <WaButton kind="pro" utm={utm} label="Richiedi informazioni" testId="cta-individuale" />
          </div>
        </div>
        <p className="text-sm text-[#3D2140]/55 mt-6 italic">Tutte le informazioni economiche e le modalità di pagamento vengono comunicate privatamente su WhatsApp.</p>
      </Section>

      {/* TESTIMONIANZA REALE (già pubblicata sul sito, sezione Dicono di me) */}
      <Section testId="testimonianze">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-editorial italic text-2xl md:text-3xl text-[#3D2140] leading-snug mb-6">
            "Con Teresa ho deciso di intraprendere il corso sul sonno, per me molto importante. È stato veramente molto interessante, utile e formativo. Lo ritengo un corso completo."
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#3D2140]/50">Un'allieva · Corso sul Sonno — dalle recensioni pubbliche del sito</p>
        </div>
      </Section>

      {/* 11. FAQ */}
      <Section eyebrow="— Domande frequenti" title={<>Quello che mi chiedono<br /><span className="text-[#FF2768]">più spesso.</span></>} testId="faq">
        <div className="space-y-3 max-w-3xl">
          {FAQ.map((f, i) => (
            <details key={f.q} className="bg-white rounded-2xl border border-[#3D2140]/10 p-5 md:p-6 group" data-testid={`faq-${i}`}>
              <summary className="font-display text-xl text-[#3D2140] cursor-pointer list-none flex items-center justify-between gap-4">
                {f.q}
                <span className="text-[#FF2768] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-[#3D2140]/75 leading-relaxed mt-3 text-[15px]">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* 12. CTA FINALE */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-[#3D2140] text-center" data-testid="cta-finale">
        <ShieldCheck className="text-[#FFE45C] mx-auto mb-6" size={36} />
        <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.02] mb-5">
          Il tuo prossimo passo<br /><span className="text-[#FF2768]">comincia da un messaggio.</span>
        </h2>
        <p className="text-white/75 text-lg max-w-xl mx-auto mb-10">
          Scrivimi su WhatsApp: ti rispondo personalmente con il programma completo, il calendario e tutte le informazioni per partecipare.
        </p>
        <WaButton kind="pro" utm={utm} label="Richiedi informazioni" testId="cta-finale-btn" />
        <p className="text-white/40 text-xs mt-8 flex items-center justify-center gap-2">
          <img src={BRAND.logo} alt="" className="h-6 w-6 rounded-full" /> T.A.T.A. Academy · Teresa Vilardo · {BRAND.websiteLabel}
        </p>
      </section>
    </div>
  );
}
